import type { MenuTab, SideMenuProps } from '@main/core-ui';
import { useRouter } from '@main/core-ui';
import { getMessageAsString } from '@main/internationalization';
import React from 'react';
import { useIntl } from 'react-intl';

import { Tab } from './Tab';
import { useSideMenu } from './useSideMenu';
import {
  OuterWrapper,
  SideMenuFadeBottom,
  SideMenuFadeTop,
  SideMenuGroup,
  SideMenuGroupTitle,
  SideMenuItem,
  SideMenuList,
  SideMenuListItem,
  SideMenuWrapper,
  SideMenuWrapperTitle,
} from './wrappers';

/**
 * Render the side menu
 */
export const SideMenu = ({
  onSelectChange,
  selected,
  tabs,
  counts,
  showCount,
}: SideMenuProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const { selectTab } = useSideMenu({
    onSelectChange,
    selected,
    tabs,
  });
  const { queryParams } = useRouter();

  const isSelected = (child: MenuTab, parent: MenuTab): string =>
    selected === child.visualId && (!parent || queryParams.name === parent.slug)
      ? 'active'
      : '';

  return (
    <>
      <SideMenuFadeTop />
      <OuterWrapper>
        {tabs.map((parent) => (
          <SideMenuWrapper key={parent.visualId}>
            <SideMenuWrapperTitle
              id={`${parent.visualId}-title`}
              onClick={() => selectTab(parent, parent)}
            >
              {getMessageAsString(
                parent.title || ('' as any),
                formatMessage as any,
              )}
            </SideMenuWrapperTitle>

            {parent.children && (
              <SideMenuGroup role="group">
                {parent.children.map((child) =>
                  child.children ? (
                    <React.Fragment key={child.id}>
                      <SideMenuGroupTitle
                        id={`${child.visualId}-group-title`}
                        onClick={() => selectTab(child, parent)}
                      >
                        {getMessageAsString(
                          child.title || ('' as any),
                          formatMessage as any,
                        )}
                      </SideMenuGroupTitle>
                      <SideMenuList
                        aria-labelledby={`${child.visualId}-group-title`}
                        id={`${child.visualId}-menu`}
                        role={child.children?.length > 0 ? 'menu' : ''}
                      >
                        {child.children.map((grandChild) => (
                          <SideMenuListItem
                            className={isSelected(grandChild, child)}
                            aria-current={
                              isSelected(grandChild, child) === 'active'
                            }
                            id={`${grandChild.visualId}-side-menu-list-item`}
                            key={grandChild.visualId}
                            role="menuitem"
                          >
                            <Tab
                              tab={{
                                ...grandChild,
                                count:
                                  counts && counts[grandChild.id]
                                    ? counts[grandChild.id].count
                                    : 0,
                                hasRequestFiles:
                                  !!counts?.[grandChild.id]?.hasRequestFiles,
                                showCount,
                              }}
                              onClick={() => selectTab(grandChild, child)}
                              isSelected={
                                isSelected(grandChild, child) === 'active'
                              }
                            />
                          </SideMenuListItem>
                        ))}
                      </SideMenuList>
                    </React.Fragment>
                  ) : (
                    <SideMenuItem
                      className={isSelected(child, parent)}
                      id={`${child.visualId}-side-menu-list-item`}
                      key={child.id}
                    >
                      <Tab
                        tab={child}
                        onClick={() => selectTab(child, parent)}
                        isSelected={isSelected(child, parent) === 'active'}
                      />
                    </SideMenuItem>
                  ),
                )}
              </SideMenuGroup>
            )}
          </SideMenuWrapper>
        ))}
      </OuterWrapper>
      <SideMenuFadeBottom />
    </>
  );
};
